import {
  CLAIM_STAGES,
  PAYMENT_CHANNELS,
} from '../../resources/enums';
import { IClaimVehiclePassenger, IDocument, ITask } from '../../resources/interfaces';
import { Company } from '../company/Company';
import { Customer } from '../customer/Customer';
import { Policy } from '../policy/Policy';
import { User } from '../User';
import { IClaim, IClaimActivityStreams, IClaimMetaData, IClaimUpdate, IClaimVehicle } from './IClaim';

export class Claim {
  accountName: string;
  accountNumber: string;
  accountProvider: string;
  accountType: PAYMENT_CHANNELS | string;
  activityStreams: IClaimActivityStreams[];
  additionalInformation: string;
  adjusted: boolean;
  agreementDate: Date | null;
  amount: string;
  approvedAmount: string | null;
  assessed: boolean;
  assessedOn: Date | null;
  assessorNotes: string | null;
  assignedStaff: User | null;
  affectedPedestrians: IClaimVehiclePassenger[];
  affectedVehicles: IClaimVehicle[];
  broker: Company | null
  claimNumber: string;
  claimAmount: number;
  claimStage: CLAIM_STAGES;
  claimType: string;
  claimDetails: { key: any; value: any }[];
  claimantVehicle: IClaimVehicle;
  createdOn: Date | undefined;
  culpableIndividual: string;
  customer: Customer;
  customerFullName: string;
  documents?: IDocument[];
  hasAgreed: boolean;
  id: string;
  incidentDate: Date | null;
  incidentDescription: string;
  incidentLocation: string;
  intendedProsecution: boolean;
  insurer: Company | null;
  isOtherPedestriansInvolved: boolean;
  isOtherVehiclesInvolved: boolean;
  isPoliceInvolved: boolean;
  metaData: IClaimMetaData;
  natureOfIncidents: string[];
  ncdEffect: string;
  new: boolean;
  paid: boolean;
  paidAmount: string | null;
  paidOn: Date | null;
  policy: Policy | null;
  policyCategory: string;
  policyNumber: string;
  policyType: string;
  coverType: string;
  notes: IClaimUpdate[];
  policeExemptionReason: string | null;
  policeOfficerName: string | null;
  policeReport: string | null;
  policeStationAddress: string | null;
  prosecutionDetails: string;
  reference: string;
  rejectionReason: string | null;
  reportDateTime: Date | null;
  reportTime: string;
  signature: string | null;
  submitted: boolean;
  submittedOn: Date | null;
  supportingDocuments: string | null;
  tasks: ITask[];
  updatedOn: Date | null;


  constructor(Imodel: IClaim) {
    this.accountName = Imodel.accountName;
    this.accountNumber = Imodel.accountNumber;
    this.accountProvider = Imodel.accountProvider;
    this.accountType = Imodel.accountType;
    this.activityStreams = Imodel.activityStreams ?? [];
    this.additionalInformation = Imodel.additionalInformation;
    this.affectedPedestrians = Imodel.affectedPedestrians ?? [];
    this.affectedVehicles = Imodel.affectedVehicles ?? [];
    this.agreementDate = Imodel.agreementDate ? new Date(Imodel.agreementDate) : null;
    this.amount = Imodel.amount;
    this.approvedAmount = Imodel.approvedAmount ?? null;
    this.assessed = Imodel.assessed ?? false;
    this.assessedOn = Imodel.assessedOn ? new Date(Imodel.assessedOn) : null;
    this.assessorNotes = Imodel.assessorNotes ?? null;
    this.assignedStaff = Imodel.assignedStaff ? new User(Imodel.assignedStaff) : null;
    this.adjusted = Imodel.adjusted ?? false;
    this.broker = Imodel.broker ? new Company(Imodel.broker) : null;
    this.claimNumber = Imodel.claimNumber;
    this.claimAmount = Imodel.claimAmount;
    this.claimStage = Imodel.claimStage as CLAIM_STAGES;
    this.claimType = Imodel.claimType;
    this.claimDetails = Imodel.claimDetails;
    this.claimantVehicle = {
      ...Imodel.claimantVehicle,
      driver: {
        ...Imodel.claimantVehicle?.driver,
        dateOfBirth: Imodel.claimantVehicle?.driver?.dateOfBirth
          ? new Date(Imodel.claimantVehicle?.driver?.dateOfBirth)
          : null,
        licenseIssuedDate: Imodel.claimantVehicle?.driver?.licenseIssuedDate
          ? new Date(Imodel.claimantVehicle?.driver?.licenseIssuedDate)
          : null
      },
      policeInvoled: Imodel?.claimantVehicle?.reasonNotInvolvingPolice ? false : true,
      dateReported: Imodel?.claimantVehicle?.dateReported
        ? new Date(Imodel.claimantVehicle?.dateReported)
        : null,
      isVehicleMultiInsured: Imodel.claimantVehicle?.isVehicleMultiInsured ? true : false,
      photos: Imodel.claimantVehicle?.photos
    };
    this.createdOn = Imodel.createdOn ? new Date(Imodel.createdOn) : undefined;
    this.culpableIndividual = Imodel.culpableIndividual;
    this.customer = new Customer(Imodel.customer);
    this.customerFullName = Imodel.customerFullName || Imodel.customer.fullName || `${Imodel.customer?.firstName} ${Imodel.customer?.lastName}`;
    this.documents = Imodel.documents?.map((document: IDocument) => ({
      id: document.id,
      title: document.title,
      docType: document.docType,
      docUrl: document.docUrl || document.docURL,
    }));
    this.hasAgreed = Imodel.hasAgreed;
    this.id = Imodel.id;
    this.incidentDate = Imodel.incidentDate ? new Date(Imodel.incidentDate) : null;
    this.incidentDescription = Imodel.incidentDescription;
    this.incidentLocation = Imodel.incidentLocation;
    this.intendedProsecution = Imodel.intendedProsecution ?? false;
    this.insurer = Imodel.insurer ? new Company(Imodel.insurer) : null;
    this.isOtherPedestriansInvolved = Imodel.isOtherPedestriansInvolved ?? false;
    this.isOtherVehiclesInvolved = Imodel.isOtherVehiclesInvolved ?? false;
    this.isPoliceInvolved = Imodel.isPoliceInvolved ?? false;
    this.metaData = Imodel.metaData;
    this.natureOfIncidents = Imodel.natureOfIncidents;
    this.ncdEffect = Imodel.ncdEffect ?? '';
    this.new = Imodel.new ?? false;
    this.paid = Imodel.paid;
    this.paidAmount = Imodel.paidAmount ?? null;
    this.paidOn = Imodel.paidOn ? new Date(Imodel.paidOn) : null;
    this.policy = Imodel.policy ? new Policy(Imodel.policy) : null;
    this.policyCategory = Imodel.policyCategory ?? '';
    this.policyNumber = Imodel.policyNumber ?? '';
    this.policyType = Imodel.policyType ?? '';
    this.notes = Imodel.notes ?? [];
    this.coverType = Imodel.coverType ?? '';
    this.policeExemptionReason = Imodel.policeExemptionReason ?? null;
    this.policeOfficerName = Imodel.policeOfficerName ?? null;
    this.policeReport = Imodel.policeReport ?? null;
    this.policeStationAddress = Imodel.policeStationAddress ?? null;
    this.prosecutionDetails = Imodel.prosecutionDetails ?? null;
    this.reference = Imodel.reference;
    this.rejectionReason = Imodel.rejectionReason ?? null;
    this.reportDateTime = Imodel.reportDateTime ? new Date(Imodel.reportDateTime) : null;
    this.reportTime = Imodel.reportTime;
    this.signature = Imodel.signature ?? null;
    this.submitted = Imodel.submitted ?? false;
    this.submittedOn = Imodel.submittedOn ? new Date(Imodel.submittedOn) : null;
    this.supportingDocuments = Imodel.supportingDocuments ?? null;
    this.tasks = Imodel.tasks;
    this.updatedOn = Imodel.updatedOn ? new Date(Imodel.updatedOn) : null;
  }

  isNew = (): boolean => this?.claimStage === CLAIM_STAGES.NEW;
  isAssessed = (): boolean => this?.claimStage === CLAIM_STAGES.ASSESSED;
  isAcknowledged = (): boolean => this?.claimStage === CLAIM_STAGES.ACKNOWLEDGED;
  isPaid = (): boolean => this?.claimStage === CLAIM_STAGES.PAID;
  isAdjusted = (): boolean => this?.claimStage === CLAIM_STAGES.ADJUSTED;
  isDvIssued = (): boolean => this?.claimStage === CLAIM_STAGES.DV_ISSUED;
  isSubmitted = (): boolean => this?.claimStage === CLAIM_STAGES.SUBMITTED;
  isInReview = (): boolean => this?.claimStage === CLAIM_STAGES.IN_REVIEW;
}
